import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryAvailableLocales } from 'src/hooks/useQueryAvailableLocales';
import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { AvailableLocalesQueryKey } from 'src/queries/queryAvailableLocales';
import { config } from 'src/lib/config';

export type ResultType = Omit<UseQueryResult<string[], Error>, 'data'> & {
    availableLocales: string[];
};

export const useAvailableLocales = (
    productKey: string | undefined | null,
    productVersion: number | null,
    options?: UseQueryOptions<string[], Error, string[], AvailableLocalesQueryKey>,
): ResultType => {
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const {
        data: availableLocalesResult,
        ...rest
    } = useQueryAvailableLocales(accessToken, {
        productKey: productKey as string,
        productVersion: productVersion as number,
    }, {
        ...options,
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        enabled: !!productKey && !!accessToken && productVersion !== null,
    });

    const availableLocales = useMemo(() => {
        const localesArray = availableLocalesResult || [];

        if (!localesArray.length) {
            return config.locales; // Fallback to all locales if no data
        }

        const availableCountries = localesArray.map((country) => country.toUpperCase());

        return config.locales.filter((locale) => {
            const countryCode = locale.split('-')[1]?.toUpperCase();

            return countryCode && availableCountries.includes(countryCode);
        });
    }, [availableLocalesResult]);

    return {
        availableLocales,
        ...rest,
    };
};
